import { env } from '@/config/env'
import { mediaUrl } from '@/lib/utils'
import { DsAvatar, DsBadge, DsButton, DsCard, DsEmptyState, DsIcons, DsMoney, dsMoneyText, DsPage, DsReceipt, DsSuccessView, DsText, DsToast } from '@/ds'
import { isOnlineGroup, leaseDueAmount, successShareText } from '@/features/payments/pay-rent-utils'
import { payRentStrings as s } from '@/features/payments/pay-rent-strings'
import type { PayRentVm } from '@/features/payments/usePayRent'
import { tenantName } from '@/features/lease/lease-utils'
import type { Lease } from '@/types/models'
import { appLocale } from '@/i18n/locale.store'

// A rent payment is in its lease's currency (the backend copies it from the property).
const moneyIn = (currency?: string) => (n: number) => dsMoneyText(n, { currency })
const dateTime = new Intl.DateTimeFormat(appLocale(), { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })

/** Pieces shared by the record / pay page and the desktop drawer. */
export function LeaseCard({ vm, lease }: { vm: PayRentVm; lease: Lease }) {
  const money = moneyIn(lease.currency)
  const due = leaseDueAmount(lease)
  return (
    <DsCard variant="muted" className="flex items-center gap-3">
      {vm.isLandlord ? (
        <>
          <DsAvatar name={tenantName(lease)} imageUrl={mediaUrl(lease.tenant?.avatar, env.mediaHost)} />
          <div className="min-w-0 flex-1">
            <DsText variant="bodyStrong">{tenantName(lease)}</DsText>
            <DsText variant="small" muted>{lease.property?.title}</DsText>
          </div>
        </>
      ) : (
        <div className="min-w-0 flex-1">
          <DsText variant="bodyStrong">{lease.property?.title}</DsText>
          <DsText variant="small" muted>{money(lease.rent)} {s.frequencyShort[lease.frequency]}</DsText>
        </div>
      )}
      {due > 0 ? <DsMoney amount={due} currency={lease.currency} size="sm" tone="warning" bottomLabel={s.due} align="end" /> : <DsBadge label={s.upToDate} tone="success" size="xs" icon={DsIcons.check} />}
    </DsCard>
  )
}



/** In place of the form when the lease takes no payment (LP-651): ended, or not active yet. */
export function LeaseNotPayable({ lease }: { lease: Lease }) {
  const ended = lease.status === 'canceled' || lease.status === 'expired'
  return <DsEmptyState icon={DsIcons.lease} title={ended ? s.leaseEndedTitle : s.leaseNotActiveTitle} description={ended ? s.leaseEndedDescription : s.leaseNotActiveDescription} compact />
}

/** The success receipt with Share; `page` wraps it in a DsPage (the phone route). */
export function PaymentSuccess({ vm, onDone, page = true }: { vm: PayRentVm; onDone: () => void; page?: boolean }) {
  const r = vm.result!
  const money = moneyIn(r.lease.currency)
  const title = vm.isLandlord ? s.successTitleLandlord : s.successTitleTenant
  const description = vm.isLandlord ? s.successOffline(tenantName(r.lease)) : isOnlineGroup(r.group) ? s.successSent : s.successPendingLandlord
  const paidOn = dateTime.format(r.paidAt)
  const share = async () => {
    const text = successShareText(title, money(r.amount), r.lease.property?.title ?? '', vm.isLandlord ? tenantName(r.lease) : undefined, paidOn)
    try {
      if (typeof navigator.share === 'function') await navigator.share({ title, text })
      else {
        await navigator.clipboard.writeText(text)
        DsToast.success(s.receiptCopied)
      }
    } catch {
      /* dismissed */
    }
  }
  // A claim the landlord has not accepted is not a receipt (LP-695): the amount and "awaiting confirmation", no date, no Share.
  if (!r.settled) {
    const pending = (
      <DsSuccessView title={title} description={description} primaryLabel={s.done} onPrimary={onDone}>
        <div className="flex flex-col items-center gap-1">
          <DsMoney amount={r.amount} currency={r.lease.currency} size="lg" />
          <DsText variant="small" muted>{s.awaitingConfirmation}</DsText>
        </div>
      </DsSuccessView>
    )
    return page ? <DsPage title={title}>{pending}</DsPage> : pending
  }
  const view = (
      <DsSuccessView title={title} description={description} primaryLabel={s.done} onPrimary={onDone} secondaryLabel={vm.isLandlord ? s.recordAnother : undefined} onSecondary={vm.reset}>
        <DsReceipt
          rows={[{ label: s.property, value: r.lease.property?.title ?? '' }, ...(vm.isLandlord ? [{ label: s.paidBy, value: tenantName(r.lease) }] : []), { label: s.paidOn, value: paidOn }]}
          total={{ label: s.total, value: money(r.amount), strong: true }}
          note={s.receiptNote}
          footer={<DsButton variant="secondary" label={s.shareReceipt} leadingIcon={DsIcons.share} onClick={() => void share()} fullWidth />}
          className="w-full text-left"
        />
      </DsSuccessView>
  )
  return page ? <DsPage title={title}>{view}</DsPage> : view
}
